import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../store/useUser';
import { getMedcallAccess, isMedcallPath } from '@/pages/medcall/medcallAccess';
import { useHelpSidebarVisibility } from '../store/useHelpSidebarVisibility';
import { getUserPolicyList, policyMatches } from '../utils/policies';
import { visibleAdminPanels } from '@/pages/admin/AdminCenter';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { ChevronDown } from "lucide-react"
import { menuItems } from "./menuItems"

const PRIMARY_NAV_LABELS = [
  'Home',
  'New Client',
  'Billing',
  'Administration',
  'Candidates/Census',
  'Message Center',
  'Reports',
  'Settings',
  'Tools',
  'Vaccines',
  'Help',
  'Log Out',
];

const compareMenuItems = (left: any, right: any) => {
  const leftLabel = `${left?.label || ''}`.trim();
  const rightLabel = `${right?.label || ''}`.trim();
  return leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
};

export const MenuBar = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const { isHelpSidebarVisible, toggleHelpSidebar } = useHelpSidebarVisibility();
  const menuButtonClassName = "h-7 gap-1.5 px-2 text-xs font-medium text-gray-700 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:text-[#0078d4] hover:data-[state=open]:text-[#106ebe]";
  const activeMenuButtonClassName = "text-[#0078d4] hover:text-[#106ebe]";

    const policyList = useMemo(() => {
      return getUserPolicyList(user);
    }, [user]);

    const wildcard = policyList.includes('*:*~*');

    const hasRequiredPolicies = (required: any) => {
      if (wildcard) return true;
      if (!required || !required.length) return true;
      if (policyList.length === 0) return false;
      return required.some((p: any) =>
        policyList.some((u: any) => policyMatches(u, p))
      );
    };

    const hasRequiredPoliciesForItem = (item: any) => {
      if (isMedcallPath(item?.path)) return getMedcallAccess(user) !== 'none';
      if (item?.anyAuthenticated) {
        return true;
      }

      const hasPolicyRequirement = Array.isArray(item?.policies) && item.policies.length > 0;
      if (!hasPolicyRequirement) {
        return typeof item?.path !== 'string';
      }

      if (hasRequiredPolicies(item.policies)) {
        return true;
      }

      return false;
    };
  
  const filteredMenuItems = (() => {
    if (!user) return [];

    const result = [];
    let shouldIncludeItems = true;

    for (const item of menuItems) {
      if (item.type === 'separator') {
        shouldIncludeItems = hasRequiredPoliciesForItem(item);

        if (!shouldIncludeItems) {
          continue;
        }

        if (!item.submenu) {
          result.push(item);
          continue;
        }

        const filteredSubmenu = item.submenu.filter((subItem: any) =>
          hasRequiredPoliciesForItem(subItem)
        );

        if (filteredSubmenu.length === 0) {
          continue;
        }

        result.push({
          ...item,
          submenu: [...filteredSubmenu].sort(compareMenuItems),
        });
        continue;
      }

      if (item.type === 'item' && hasRequiredPoliciesForItem(item)) {
        result.push(item);
        continue;
      }

      if (item.type === 'action') {
        result.push(item);
      }
    }

    return result;
  })();

  const administrationRootItem = filteredMenuItems.find((item: any) => item.label === 'Administration');
  const adminFavoriteIds = Array.isArray(user?.settings?.adminSettingsFavorites)
    ? user.settings.adminSettingsFavorites
    : [];
  const canAccessAdminPanel = (panel: any) => {
    const requiredPolicies = Array.isArray(panel?.policies) && panel.policies.length > 0
      ? panel.policies
      : [];
    if (panel?.anyAuthenticated) {
      return true;
    }
    if (requiredPolicies.length === 0) {
      return false;
    }
    return hasRequiredPolicies(requiredPolicies);
  };
  const accessibleAdminPanels = visibleAdminPanels
    .filter((panel) => canAccessAdminPanel(panel))
    .sort((left, right) => compareMenuItems({ label: left.title }, { label: right.title }));
  const favoriteAdminPanels = accessibleAdminPanels.filter((panel) => adminFavoriteIds.includes(panel.id));
  const administrationPanelMenuItems = (
    accessibleAdminPanels.length > 6 && favoriteAdminPanels.length > 0
      ? favoriteAdminPanels
      : accessibleAdminPanels
  ).map((panel) => ({
    label: panel.title,
    path: panel.path,
    icon: panel.icon,
  }));
  const administrationMenuItems = [
    {
      label: 'Admin Center',
      path: administrationRootItem?.path || '/admin',
      icon: administrationRootItem?.icon,
    },
    ...(administrationPanelMenuItems.length > 0
      ? [
          {
            type: 'separator',
            label: 'admin-center-separator',
          },
          ...administrationPanelMenuItems,
        ]
      : []),
  ];

  const primaryNavItems = PRIMARY_NAV_LABELS
    .map((label) => {
      if (label === 'Administration') {
        if (!administrationRootItem) {
          return null;
        }

        if (administrationMenuItems.length === 0) {
          return {
            ...administrationRootItem,
            submenu: [],
          };
        }

        return {
          type: 'separator',
          label: 'Administration',
          icon: administrationRootItem.icon,
          submenu: administrationMenuItems,
        };
      }

      return filteredMenuItems.find((item: any) => item.label === label);
    })
    .filter(Boolean);

  const openItem = (item: any) => {
    if (item?.type === 'action') {
      if (item.action === 'toggleHelp') {
        toggleHelpSidebar();
        return;
      }

      if (item.action === 'logout') {
        logout();
        navigate('/login');
      }
      return;
    }

    if (item?.hardlink) {
      window.open(item.path, '_blank');
      return;
    }

    if (typeof item?.path === 'string') {
      navigate(item.path);
    }
  };

  const renderPrimaryNavItem = (item: any) => {
    const ItemIcon = item.icon;
    const isHelpItemOpen = (
      item?.action === 'toggleHelp'
      || item?.submenu?.some((subItem: any) => subItem?.action === 'toggleHelp')
    ) && isHelpSidebarVisible;

    if (item.type === 'separator' && item.submenu?.length) {
      return (
        <DropdownMenu key={item.label}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(menuButtonClassName, isHelpItemOpen && activeMenuButtonClassName)}
            >
              {ItemIcon ? <ItemIcon className="h-3.5 w-3.5" /> : null}
              <span>{item.label}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {item.submenu.map((subItem: any) => (
              subItem.type === 'separator' ? (
                <DropdownMenuSeparator key={subItem.label} />
              ) : (
                <DropdownMenuItem
                  key={subItem.label}
                  onClick={() => openItem(subItem)}
                  className="text-sm text-gray-700 py-1 px-2 hover:bg-gray-100"
                >
                  {subItem.icon ? <subItem.icon className="mr-2 h-4 w-4" /> : null}
                  <span>{subItem.label}</span>
                </DropdownMenuItem>
              )
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <Button
        key={item.label}
        variant="ghost"
        size="sm"
        onClick={() => openItem(item)}
        className={cn(menuButtonClassName, isHelpItemOpen && activeMenuButtonClassName)}
      >
        {ItemIcon ? <ItemIcon className="h-3.5 w-3.5" /> : null}
        {item.label}
      </Button>
    );
  };

  if (primaryNavItems.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {primaryNavItems.map(renderPrimaryNavItem)}
    </div>
  );
};
